import React, { useState, useEffect } from "react";
import withLanguageContext from "lib/hocs/withLanguageContext";
import SettingStore from "stores/settings";
import config from "lib/config";
import format from "lib/format";
import { Typography } from "Components/Base/DataDisplay/Typography";
import { Box } from "Components/Base/Layout/Box";
import { Grid } from "Components/Base/Layout/Grid";
import ImageCard from "Components/Fragment/ImageCard";
import { LinearProgress } from "Components/Base/Feedback/LinearProgress";
import * as CONSTANTS from "domains/programs/constants";
import * as MainMenu from "lib/constants/mainmenu";
import {
  StyledEmptyProgress,
  StyledCardBanner,
  StyledEmptyTag,
  StyledEmptyStars,
  StyledEmptyActivityTitle,
} from "domains/programs/functions";
import Rating from "lib/components/Rating";
import { Link } from "Components/Base/Navigation/Link";
import styled from "styled-components";

const isPortalOverview =
  location && location.pathname.includes("/activitycentre/activitycentre.aspx");

const StyledProgramGrid = styled(Grid)`
  &.MuiGrid-root.MuiGrid-container {
    margin-top: 0;
    margin-left: 0;

    .MuiGrid-item {
      padding-top: 0;
      padding-left: 0;
    }
  }

  > .MuiGrid-item {
    max-width: 300px;
    min-width: 300px;
    margin: 0 24px 24px 0;
    height: 250px;
  }

  .MuiCardContent-root {
    padding: 0;
  }

  .card-course-icon.program-icon {
    top: 5px;
    left: 5px;
  }

  h4 {
    margin: 0;
  }
`;

const ProgramCards = (props) => {
  const { programs, languageContext, onClick, managerView, personalId } = props;
  const [currentPrograms, setCurrentPrograms] = useState([]);

  const updateRating = (newRating, newComment, programId) => {
    let updatedPrograms = currentPrograms;
    updatedPrograms.forEach((pr) => {
      if (pr.courseId == programId) {
        pr.personalRating.comment = newComment;
        pr.personalRating.rating = newRating;
      }
    });
    setCurrentPrograms(updatedPrograms);
  };

  useEffect(() => {
    setCurrentPrograms(programs);
  }, [programs]);

  const WarningTag = ({ program }) => {
    if (program.requested) {
      return (
        <span className="tag-warning">
          {languageContext("programrequested")}
        </span>
      );
    }

    if (
      !program.completed &&
      program.attestStatus === CONSTANTS.ATTEST_WAITING
    ) {
      return (
        <span className="tag-warning">
          {languageContext("waitingforattest")}
        </span>
      );
    }

    return <StyledEmptyTag className="card-empty-tag" />;
  };

  const cards = currentPrograms?.map((program) => {
    let programUrl = `${config.urlRoot}#/edu/program?activityId=${program.activityId}`;

    /*}${personalId ? `&id=${personalId}` : null}${
      !isPortalOverview && "&prev=" + MainMenu.ID_COWORKER_MYCOURSES
    }`;*/
    programUrl = personalId ? programUrl + "&id=" + personalId : programUrl;
    programUrl = isPortalOverview
      ? programUrl
      : programUrl + "&prev=" + MainMenu.ID_COWORKER_MYCOURSES;

    const {
      title,
      activityTitle,
      progress,
      courseId,
      activityId,
      completed,
      attestStatus,
      microsoftTeamId,
    } = program;

    const withProgress = progress >= 0 && progress <= 100;

    const displayImage =
      SettingStore.getSettingValue(
        CONSTANTS.SYSTEM_SETTING_SHOW_COURSE_IMAGE_PORTAL,
      ) === CONSTANTS.SYSTEM_SETTING_ON;

    const defaultImageUrl = isPortalOverview
      ? CONSTANTS.PROGRAM_IMAGE_PORTALOVERVIEW
      : CONSTANTS.PROGRAM_IMAGE;

    const imageUrl = program.image
      ? format.url(
          `/utils/streamfile.aspx?type=courseobjectimage&param=${courseId}/${program.image}`,
        )
      : defaultImageUrl;

    const CardLink = ({ label, href }) => (
      <Typography>
        <Link
          style={{ display: "flex", alignItems: "center" }}
          color="inherit"
          href={href}
        >
          {label}
        </Link>
      </Typography>
    );

    const CardTitle = () => (
      <>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <div className="card-course-icon program-icon" />
        </Box>
        <Box mt={2} className="elipssis" title={title}>
          {title}
        </Box>
      </>
    );

    const CardActions = () => (
      <Box display="flex" justifyContent="center" width="100%">
        {managerView && !program.requested && (
          <div
            tabIndex="0"
            className="underline pointer"
            onClick={() => onClick(activityId)}
          >
            {languageContext("showprogram")}
          </div>
        )}

        {(program.requested || attestStatus === CONSTANTS.ATTEST_WAITING) &&
          !managerView && (
            <CardLink
              label={languageContext("registerHere")}
              href={format.url(
                "/activities/ActivityDetails.aspx?inapp=1&id=" +
                  program.courseId,
              )}
            />
          )}

        {!managerView &&
          !program.requested &&
          attestStatus !== CONSTANTS.ATTEST_WAITING && (
            <CardLink
              href={programUrl}
              label={languageContext("showprogram")}
            />
          )}
      </Box>
    );

    return {
      title: title,
      card: (
        <ImageCard
          actions={<CardActions />}
          image={displayImage && imageUrl}
          title={<CardTitle />}
          hovertext={title}
          className="card"
        >
          {!completed && microsoftTeamId && (
            <StyledCardBanner>
              <span className="tag-microsoftTeams" id="cardBannerMSTeams">
                {languageContext("connectedToTeams")}
              </span>
            </StyledCardBanner>
          )}
          {completed && (
            <StyledCardBanner>
              <span className="tag-completed">
                {languageContext("completed")}
              </span>
            </StyledCardBanner>
          )}
          <WarningTag program={program} />
          {activityTitle ? (
            <h4 className="italic elipssis" title={activityTitle}>
              {activityTitle}
            </h4>
          ) : (
            <StyledEmptyActivityTitle />
          )}
          {program.allowRating ? (
            <Rating
              currentRating={program.personalRating.rating}
              currentComment={program.personalRating.comment}
              type={5}
              objectId={program.courseId}
              updateRating={updateRating}
              readOnly={false}
              managerView={managerView}
            />
          ) : (
            <StyledEmptyStars />
          )}
          <div>
            {withProgress ? (
              <LinearProgress
                id="programLinearProgress"
                value={progress}
                title={`${languageContext("completed2")}: ${progress}%`}
                colorOverride={CONSTANTS.COURSE_COLOR_PROGRESS_HIGH}
              />
            ) : (
              <StyledEmptyProgress />
            )}
          </div>
        </ImageCard>
      ),
    };
  });

  return (
    <StyledProgramGrid container spacing={3}>
      {cards?.map(({ card, title }, index) => (
        <Grid item xs={6} key={title + index}>
          {card}
        </Grid>
      ))}
    </StyledProgramGrid>
  );
};

export default withLanguageContext(ProgramCards, "Programs");
